import React, { FC, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Appearance, Button } from '@src/components/Button';
import CloseSvg from '@src/svgs/fr-close.svg';
import { trackEvent } from '@src/metrics';
import { useAppDispatch, useAppSelector } from '@src/redux';
import { closeModal } from '@src/redux/actions/modal';
import {
  useCustomerForActivateSubscriptionQuery,
  useActivateSubscriptionMutation,
  CustomerSubscriptionCycleEnum,
  PaymentMethodTypeEnum,
  CustomerPlanEnum,
} from '@src/graphql/generated';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { CircularLoading, Size } from '@src/components/CircularLoading';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { fetchNotice } from '@src/redux/actions/notice';
import { getRecruiter } from '@src/redux/selectors/recruiter';
import { SUBSCRIBE_ERROR_MESSAGES, STATIC_MEDIA_DOMAIN } from '@src/constants';
import { Image } from '@src/components/Image';
import { fetchCustomerStatus } from '@src/redux/actions/recruiter';
import { CreditCards } from './CreditCards';
import { ActivateFreemiumModalProps } from './props';
import './styles.scss';

const IMAGE_SRC = `${STATIC_MEDIA_DOMAIN}/images/logos.png`;

export const ActivateFreemiumModal: FC<ActivateFreemiumModalProps> = ({ onCompleted }) => {
  const dispatch = useAppDispatch();

  const [isInvoiceFormVisible, setIsInvoiceFormVisible] = useState<boolean>(false);

  const recruiter = useAppSelector(getRecruiter);

  const {
    loading: isLoading,
    error,
    data,
  } = useCustomerForActivateSubscriptionQuery({
    fetchPolicy: 'network-only',
    onError: () => dispatch(setErrorFlashMessage()),
  });
  const [activateSubscription] = useActivateSubscriptionMutation();

  if (isLoading) {
    return (
      <div className="activate-subscription-modal">
        <CircularLoading className="activate-subscription-modal__loading" size={Size.LARGE} />
      </div>
    );
  }

  if (error) {
    return null;
  }

  async function onPaymentMethodAdded(methodType: PaymentMethodTypeEnum): Promise<void> {
    trackEvent({
      context: MetricContext.ACTIVATE_SUBSCRIPTION,
      interaction: MetricAction.SUBSCRIBE,
      payload: { 'Plan Type': 'freemium' },
    });

    try {
      await activateSubscription({
        variables: {
          planType: CustomerPlanEnum.FREEMIUM,
          subscriptionCycle: CustomerSubscriptionCycleEnum.MONTHLY,
        },
      });

      await dispatch(fetchCustomerStatus());

      // fetch notice again to hide top bar
      await dispatch(fetchNotice());

      dispatch(setSuccessFlashMessage('Subscription has successfully activated'));

      if (methodType === PaymentMethodTypeEnum.INVOICE) {
        dispatch(closeModal());
        onCompleted && onCompleted();
      } else {
        setIsInvoiceFormVisible(true);
      }
    } catch (error) {
      if (error instanceof ApolloError && error.graphQLErrors) {
        error.graphQLErrors.forEach(graphQLError => {
          const code: keyof typeof SUBSCRIBE_ERROR_MESSAGES = graphQLError?.extensions?.exception.code;

          if (code) {
            dispatch(setErrorFlashMessage(SUBSCRIBE_ERROR_MESSAGES[code]));
          }
        });
      }

      throw error; // throw error, not to clear card values if mutation returns error code
    }
  }

  const customer = data?.customer;

  return (
    <div className="activate-subscription-modal-new">
      <CreditCards
        closeModal={() => dispatch(closeModal())}
        isInvoiceFormVisible={isInvoiceFormVisible}
        isPayByInvoiceEnabled={!!customer?.isPayByInvoiceEnabled}
        onCompleted={onCompleted}
        onPaymentMethodAdded={onPaymentMethodAdded}
        recruiterName={recruiter.firstName}
      />

      <Button
        appearance={Appearance.SECONDARY}
        className="activate-subscription-modal-new__close"
        icon={CloseSvg}
        onClick={() => dispatch(closeModal())}
      />

      <div className="activate-subscription-modal-new__company-logos-wrapper">
        <Image className="activate-subscription-modal-new__logos" src={IMAGE_SRC} />
      </div>
    </div>
  );
};
